import { LitElement, html, css, nothing, type TemplateResult } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { EVENT } from './events'

/**
 * Designed for Easy Pricing tooltip in mind to take in html strings
 */

@customElement('rq-tooltip')
export class Tooltip extends LitElement {
  static styles = css`
    :host {
      position: relative;
      display: inline-block;
      display: flex;
      justify-content: center;
    }
    .lh-tooltip {
      display: inline-block;
    }
    .trigger {
      cursor: pointer;
      display: inline-block;
      position: relative;
      margin-right: 2px;
      max-width: fit-content;
    }
    .trigger::after {
      background-image: linear-gradient(
        90deg,
        var(--mode-tooltip-trigger-color, #00aeef),
        var(--mode-tooltip-trigger-color, #00aeef) 65%,
        transparent 65%,
        transparent 100%
      );
      background-size: 8px 1px;
      border: none;
      content: '';
      display: var(--rq-tooltip-line, block);
      height: 1px;
      position: absolute;
      width: 100%;
    }
    .tooltip-content {
      visibility: hidden;
      display: none;
      position: absolute;
      background-color: var(
        --rq-tooltip-background-color,
        var(--lh-color-neutral-900, #111313)
      );
      padding: 32px;
      color: var(--lh-color-neutral-900, #111313);
      font-family: var(--lh-font-family-sans-serif);
      font-size: var(--lh-tooltip-font-size, 0.75rem);
      left: var(--rq-tooltip-content-left, auto);
      right: var(--rq-tooltip-content-right, auto);
      top: var(--rq-tooltip-content-top, -50%);
      transform: translateY(-50%);
      line-height: var(--lh-line-height-1-333);
      width: var(--rq-tooltip-width, fit-content);
      max-width: var(--rq-tooltip-max-width, 300px);
      padding: var(--lh-tooltip-padding, 8px);
      z-index: 10;
      border: var(--rq-tooltip-border, none);
      border-radius: var(--lh-tooltip-border-radius, 0.25rem);
    }

    .tooltip-content::after {
      content: '';
      display: var(--rq-arrow-display, block);
      position: absolute;
      top: 50%;
      left: var(--rq-tooltip-arrow-left, auto);
      right: var(--rq-tooltip-arrow-right, auto);
      transform: var(--rq-tooltip-arrow-transform, translate(-100%, -50%));
      border-width: 5px;
      border-style: solid;
      border-color: var(
        --rq-tooltip-arrow-border-color,
        transparent var(--lh-color-neutral-900, #111313) transparent transparent
      );
    }
    .header,
    .description {
      white-space: pre-wrap;
      color: var(--rq-tooltip-text-color, var(--lh-color-neutral-900, #111313));
      font-size: var(--lh-tooltip-font-size, 0.75rem);
    }
    .description {
      display: flex;
      flex-direction: column;
      gap: 4px;
      align-items: flex-start;
      /* Add configurable bottom spacing below description content */
      margin-bottom: var(--lh-tooltip-bottom-spacing, -24px);
    }
    .show {
      visibility: visible;
      opacity: 1;
      transition: opacity 300ms;
      display: block;
    }
    p,
    ul,
    li {
      margin: 0;
      padding-left: 10px;
    }
    ul {
      padding-top: 4px;
    }
    .line-item {
      display: grid;
      grid-template-columns: 20px 50px auto;
      align-items: center;
      gap: 8px;
    }
  `

  @property()
  header!: string | TemplateResult

  @property()
  description!: string

  @property()
  position = 'right'

  @property({ attribute: 'background-color' })
  backgroundColor = 'var(--Neutral-White, #FFF)'

  @property({ attribute: 'border-color' })
  borderColor?: string

  @property({ attribute: 'hide-line', type: Boolean })
  hideLine: boolean = false

  @query('.description')
  descriptionElement!: HTMLDivElement

  @query('.trigger')
  triggerElement!: HTMLDivElement

  @state()
  open: boolean = false

  connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener(EVENT.MOUSEENTER, () => {
      this.open = true
    })
    this.addEventListener(EVENT.MOUSELEAVE, () => {
      this.open = false
    })
    this.addEventListener(EVENT.FOCUSIN, () => {
      this.open = true
    })
    this.addEventListener(EVENT.FOCUSOUT, () => {
      this.open = false
    })
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeEventListener(EVENT.MOUSEENTER, () => {
      this.open = true
    })
    this.removeEventListener(EVENT.MOUSELEAVE, () => {
      this.open = false
    })
    this.removeEventListener(EVENT.FOCUSIN, () => {
      this.open = true
    })
    this.removeEventListener(EVENT.FOCUSOUT, () => {
      this.open = false
    })
  }

  firstUpdated(): void {
    this.updateDescription()
  }

  updated(changedProperties: Map<string, unknown>) {
    // Recompute styles when key inputs change
    if (
      changedProperties.has('description') ||
      changedProperties.has('position') ||
      changedProperties.has('backgroundColor') ||
      changedProperties.has('borderColor') ||
      changedProperties.has('hideLine')
    ) {
      this.updateDescription()
    }
    // When opening, ensure tooltip stays visible by flipping if needed
    if (changedProperties.has('open') && this.open) {
      this.maybeFlipToStayVisible()
    }
  }

  private applyPosition(position: 'left' | 'right' | 'top') {
    const arrowColor =
      this.borderColor || 'var(--lh-color-neutral-900, #111313)'
    switch (position) {
      case 'top':
        this.style.setProperty('--rq-arrow-display', 'none')
        this.style.setProperty('--rq-tooltip-content-top', '-66%')
        this.style.setProperty('--rq-tooltip-content-left', '0')
        this.style.setProperty('--rq-tooltip-content-right', 'auto')
        this.style.setProperty('--rq-tooltip-arrow-left', 'auto')
        this.style.setProperty('--rq-tooltip-arrow-right', 'auto')
        this.style.setProperty(
          '--rq-tooltip-arrow-border-color',
          `transparent  transparent transparent transparent`
        )
        break
      case 'left':
        this.style.setProperty('--rq-tooltip-content-left', 'auto')
        this.style.setProperty('--rq-tooltip-arrow-left', 'auto')
        this.style.setProperty('--rq-tooltip-content-right', 'calc(100% + 4px)')
        this.style.setProperty('--rq-tooltip-arrow-right', '0')
        this.style.setProperty(
          '--rq-tooltip-arrow-transform',
          'translate(100%, 50%)'
        )
        this.style.setProperty('--rq-tooltip-content-top', '50%')
        this.style.setProperty(
          '--rq-tooltip-arrow-border-color',
          `transparent  transparent transparent ${arrowColor}`
        )
        break
      default:
        this.style.setProperty('--rq-tooltip-content-left', 'calc(100% + 4px)')
        this.style.setProperty('--rq-tooltip-arrow-left', '0')
        this.style.setProperty('--rq-tooltip-content-right', 'auto')
        this.style.setProperty('--rq-tooltip-arrow-right', 'auto')
        this.style.setProperty(
          '--rq-tooltip-arrow-transform',
          'translate(-100%, -50%)'
        )
        this.style.setProperty('--rq-tooltip-content-top', '50%')
        this.style.setProperty(
          '--rq-tooltip-arrow-border-color',
          `transparent ${arrowColor} transparent transparent`
        )
    }
  }

  private maybeFlipToStayVisible() {
    const content = this.renderRoot.querySelector(
      '.tooltip-content'
    ) as HTMLElement | null
    if (!content || !this.open) return
    const rect = content.getBoundingClientRect()
    if (this.position === 'left' && rect.left < 0) {
      this.applyPosition('right')
    } else if (this.position === 'right' && rect.right > window.innerWidth) {
      this.applyPosition('left')
    }
  }

  private updateDescription() {
    this.descriptionElement.innerHTML = this.description
    this.style.setProperty(
      '--rq-tooltip-line',
      this.hideLine ? 'none' : 'block'
    )
    this.applyPosition((this.position as any) || 'right')

    this.style.setProperty(
      '--rq-tooltip-background-color',
      this.backgroundColor
        ? this.backgroundColor
        : 'var(--lh-color-neutral-900, #111313);'
    )
    this.style.setProperty(
      '--rq-tooltip-border',
      this.borderColor ? `1px solid ${this.borderColor}` : 'none'
    )
    // If already open, check for clipping and flip if necessary
    if (this.open) this.maybeFlipToStayVisible()
  }

  render() {
    return html`
      <div class="lh-tooltip">
        <div class="trigger" tabindex="0" aria-describedby="tooltip">
          <slot></slot>
        </div>
        <div
          style="padding: 24px; box-shadow: 0 8px 16px 0 rgba(0, 76, 108, 0.08);"
          class=${classMap({ 'tooltip-content': true, show: this.open })}
          role="tooltip"
          id="tooltip"
        >
          ${this.header
            ? html`<lh-typography
                ><span class="header"
                  ><strong>${this.header}</strong></span
                ></lh-typography
              >`
            : nothing}<lh-typography
            ><div class="description"></div
          ></lh-typography>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rq-tooltip': Tooltip
  }
}
